import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import eabLogo from "@/assets/eab-logo-horizontal.png";
import illustration from "@/assets/topshop/topshop-illustration.webp";

export function SponsorLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const { data: sponsor, isLoading } = useQuery({
    queryKey: ["sponsor", "self", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase.from("sponsors").select("full_name, organization_name").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <header className="bg-background border-b">
        <div className="mx-auto max-w-6xl flex items-center gap-3 px-4 py-3">
          <img src={eabLogo} alt="Entrepreneurs Across Borders" className="h-8 sm:h-10 w-auto" />
          <img src={illustration} alt="" className="h-9 w-9 object-contain hidden sm:block" />
          <div className="ml-auto text-right text-sm leading-tight">
            {!isLoading && (sponsor ? (
              <>
                <p className="font-medium">{sponsor.full_name}</p>
                {sponsor.organization_name && <p className="text-muted-foreground text-xs">{sponsor.organization_name}</p>}
              </>
            ) : <p className="font-medium">Admin preview</p>)}
          </div>
          <Button variant="outline" size="sm" onClick={handleSignOut}><LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sign out</span></Button>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-8">{children}</main>
      <footer className="border-t bg-background">
        <div className="mx-auto max-w-6xl px-4 py-6 text-sm text-muted-foreground flex flex-wrap gap-2 justify-between">
          <span>The Top Shop Challenge is a program of Entrepreneurs Across Borders</span>
          <a href="https://eaborders.org" target="_blank" rel="noopener noreferrer" className="underline">eaborders.org</a>
        </div>
      </footer>
    </div>
  );
}
